import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChartBar, Table } from "@phosphor-icons/react";
import { endOfDay, startOfDay, subDays } from "date-fns";
import { Schema } from "@hypertune/sdk/src/shared";
import { NavigateOptions, useSearchParams } from "react-router-dom";
import { AnalyticsViewConfig } from "@hypertune/shared-internal/src/types";
import { ProjectQuery } from "../../../generated/graphql";
import {
  CommitContext,
  ImplementationContext,
  TimeRange,
} from "../../../lib/types";
import SidebarItem from "../../../components/SidebarItem";
import matchesSearch from "../../../lib/generic/matchesSearch";
import { iconColor, iconWeight } from "../../../components/icons/icons";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  HypothesisVariant,
  resetAnalyticsEditor,
  setActiveAnalyticsView,
  setShowNewViewModal,
} from "./analyticsSlice";
import {
  analyticsFromParamKey,
  analyticsToParamKey,
  useActiveSelectedAnalyticsView,
  useSelectedAnalyticsViewId,
} from "./analyticsHooks";
import SidebarContainer from "../SidebarContainer";
import AnalyticsTopBar from "./AnalyticsTopBar";
import PlusButton from "../../../components/buttons/PlusButton";
import EmptyStateContainer from "../../../components/EmptyStateContainer";
import { useHypertune } from "../../../generated/hypertune.react";
import useSearchParamsState from "../../../app/useSearchParamsState";
import getFunnelStepPayloadObjectTypeName from "../../../lib/getFunnelStepPayloadObjectTypeName";
import {
  derivedFieldHasExpressionError,
  getDerivedFieldsCommitContext,
} from "./step/DerivedFields";
import {
  filterHasExpressionError,
  getFilterCommitContext,
} from "./step/Filter";
import { TestStatisticsDropdownOptions } from "../../../generated/hypertune";
import AnalyticsTableView from "./AnalyticsTableView";
import FunnelView from "./FunnelView";

const searchTextQueryParamKey = "analytics_search";
const focusModeEnabledPramKey = "analytics_focus_mode";

export default function AnalyticsEditor({
  meId,
  isVisible,
  canEdit,
  views,
  schema,
  implementationContext,
}: {
  meId: string;
  isVisible: boolean;
  canEdit: boolean;
  views: ProjectQuery["project"]["analyticsViews"];
  schema: Schema;
  implementationContext: ImplementationContext;
}): React.ReactElement {
  const hypertune = useHypertune();
  const content = hypertune.content().analytics();
  const dispatch = useAppDispatch();
  const editorState = useAppSelector((state) => state.projectAnalytics);

  const metricVariantNodes = hypertune
    .content()
    .analytics()
    .testStatisticsDropdownOptions();

  const metricVariants = metricVariantNodes.get();
  const [searchText, setSearchText] = useSearchParamsState(
    searchTextQueryParamKey,
    ""
  );

  const [selectedMetric, setSelectedMetric] = useState<
    keyof TestStatisticsDropdownOptions
  >(
    hypertune.features().analyticsProbabilityMetricsEnabled({ fallback: false })
      ? "frequentist95ConfidenceLevel"
      : "default"
  );

  const [selectedHypothesis, setSelectedHypothesis] =
    useState<HypothesisVariant>("One-sided");

  const activeView = useActiveSelectedAnalyticsView();
  const [selectedViewId, setSelectedViewId] = useSelectedAnalyticsViewId();

  useEffect(() => {
    dispatch(
      activeView
        ? setActiveAnalyticsView(JSON.parse(activeView.configJson))
        : resetAnalyticsEditor()
    );
  }, [dispatch, activeView]);

  const [focusModeEnabled, setFocusModeEnabled] = useSearchParamsState(
    focusModeEnabledPramKey,
    false
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const timeRange: TimeRange = useMemo(
    () => ({
      start: new Date(
        searchParams.get(analyticsFromParamKey) ??
          // 30 days ago
          startOfDay(subDays(new Date(), 29))
      ),
      end: new Date(
        searchParams.get(analyticsToParamKey) ?? endOfDay(new Date())
      ),
    }),
    [searchParams]
  );
  const setSelectedTimeRange = useCallback(
    (newTimeRange: TimeRange, navigateOpts?: NavigateOptions) => {
      setSearchParams(
        (currentSearchParams) => ({
          ...Object.fromEntries(currentSearchParams),
          [analyticsFromParamKey]: newTimeRange.start.toJSON(),
          [analyticsToParamKey]: newTimeRange.end.toJSON(),
        }),
        navigateOpts
      );
    },
    [setSearchParams]
  );
  useEffect(() => {
    // Ensure time range is initialized.
    if (
      isVisible &&
      (!searchParams.get(analyticsFromParamKey) ||
        !searchParams.get(analyticsToParamKey))
    ) {
      setSelectedTimeRange(timeRange, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const commitContext = useMemo(
    () => ({
      schema,
      ...implementationContext,
    }),
    [schema, implementationContext]
  );
  const selectedHasError = useMemo(
    () => configHasError(commitContext, editorState.config),
    [commitContext, editorState.config]
  );

  return (
    <div
      className={
        isVisible
          ? "flex- flex flex-grow items-stretch overflow-hidden"
          : "hidden"
      }
    >
      <SidebarContainer
        searchText={searchText}
        setSearchText={setSearchText}
        actions={
          canEdit ? (
            <div className="text-tx-default">
              <PlusButton onClick={() => dispatch(setShowNewViewModal(true))} />
            </div>
          ) : undefined
        }
        childrenClassName={views.length === 0 ? "pb-[5px]" : ""}
      >
        {views.length === 0 && (
          <EmptyStateContainer
            icon={<ChartBar />}
            content={content.emptyState().get()}
            buttonOnClick={
              canEdit ? () => dispatch(setShowNewViewModal(true)) : undefined
            }
          />
        )}
        {views
          .filter((view) => matchesSearch(searchText, [view.name]))
          .map((view) => {
            const isSelected = selectedViewId === view.id;
            const config = JSON.parse(view.configJson) as AnalyticsViewConfig;
            const Icon = config.type === "funnel" ? ChartBar : Table;

            return (
              <SidebarItem
                icon={
                  <Icon
                    color={iconColor(isSelected)}
                    weight={iconWeight(isSelected)}
                  />
                }
                title={view.name}
                onClick={() => setSelectedViewId(view.id)}
                isSelected={isSelected}
                className="px-3 py-[11px]"
              />
            );
          })}
      </SidebarContainer>
      <div className="flex h-full w-full flex-grow flex-col items-stretch overflow-hidden bg-bg-light bg-dotted">
        {isVisible && selectedViewId && activeView && (
          <>
            <AnalyticsTopBar
              viewId={activeView.id}
              viewName={activeView.name}
              viewLastRefreshedAt={new Date(activeView.lastRefreshedAt)}
              config={editorState.config}
              timeRange={timeRange}
              selectedMetric={selectedMetric}
              setSelectedTimeRange={setSelectedTimeRange}
              setSelectedMetric={setSelectedMetric}
              canEdit={canEdit}
              selectedHasError={selectedHasError}
              focusModeEnabled={focusModeEnabled}
              setFocusModeEnabled={setFocusModeEnabled}
              metricVariants={metricVariants}
              selectedHypothesis={selectedHypothesis}
              setSelectedHypothesis={setSelectedHypothesis}
            />
            {editorState.config.type === "funnel" ? (
              <FunnelView
                meId={meId}
                selectedViewId={selectedViewId}
                commitContext={commitContext}
                config={editorState.config}
                timeRange={timeRange}
                canEdit={canEdit}
                hasError={selectedHasError}
                focusModeEnabled={focusModeEnabled}
                selectedMetric={selectedMetric}
                selectedHypothesis={selectedHypothesis}
              />
            ) : (
              <AnalyticsTableView
                meId={meId}
                canEdit={canEdit}
                commitContext={commitContext}
                config={editorState.config}
                timeRange={timeRange}
                selectedViewId={selectedViewId}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function configHasError(
  commitContext: CommitContext,
  config: AnalyticsViewConfig
): boolean {
  if (config.type === "table") {
    return false;
  }

  return config.steps.some((step) => {
    const payloadObjectTypeName = getFunnelStepPayloadObjectTypeName(
      commitContext,
      step
    );
    if (
      step.filter &&
      filterHasExpressionError(
        getFilterCommitContext(
          commitContext.schema,
          step.type,
          payloadObjectTypeName
        ),
        step.filter
      )
    ) {
      return true;
    }
    if (!step.derivedFields) {
      return false;
    }
    const stepDerivedFieldCommitContext = getDerivedFieldsCommitContext(
      commitContext.schema,
      step.type,
      payloadObjectTypeName,
      step.derivedFields ?? []
    );

    return step.derivedFields.some((derivedField) =>
      derivedFieldHasExpressionError(
        stepDerivedFieldCommitContext,
        derivedField
      )
    );
  });
}
